import { COLOR_MASK, type Color, QUEEN, TYPE_MASK, colorBit, moveTo } from '../board.ts';
import { FROZEN_QUEEN_1, FROZEN_QUEEN_2 } from '../pieces.ts';
import type { MoveContext } from '../rules.ts';
import type { Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 6 (draft). The queen has a cooldown. After a queen moves she freezes and sits out her
// side's next two turns: a frozen queen can't move, capture or give check, but she still
// blocks and can be captured. She thaws at the end of her side's second turn and is ready
// again on the third. Queens from promotion start ready.

/** Turns a frozen queen still has to sit out (0 for any other piece). */
export function frozenTurns(piece: number): number {
  const t = piece & TYPE_MASK;
  return t === FROZEN_QUEEN_2 ? 2 : t === FROZEN_QUEEN_1 ? 1 : 0;
}

/** Squares of `c`'s frozen queens. */
export function frozenQueens(board: Int8Array, c: Color): number[] {
  const bit = colorBit(c);
  const out: number[] = [];
  for (let sq = 0; sq < 64; sq++) if (board[sq] & bit && frozenTurns(board[sq])) out.push(sq);
  return out;
}

export const cooldown: Variant = {
  id: 'cooldown',
  info: {
    name: 'Cooldown Chess',
    but: 'the queen has a cooldown',
    tagline: 'Every time your queen moves she has to rest for two turns. Make it count.',
    summary: 'After your queen moves she **freezes for your next 2 turns**: no moving, no capturing, no check. She can still be taken.',
    rules: [
      'After a queen moves, she freezes for her side’s next 2 turns.',
      'A frozen queen can’t move, capture or give check. She still blocks, and she can be captured.',
      'She thaws at the end of your second turn and can move again on the third.',
      'A pawn that promotes to a queen starts ready to go.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#4cc9f0',
    emoji: '🧊',
    bots: ['Ice Cube', 'Slush', 'Frostbite', 'Glacier', 'Ice Queen'],
  },
  startFen: STANDARD_FEN,
  extraPieces: [FROZEN_QUEEN_2, FROZEN_QUEEN_1],
  insufficientMaterial: true,

  afterMove(ctx: MoveContext) {
    const { next, us } = ctx;
    const b = next.board;
    const bit = colorBit(us);
    // The mover's resting queens count down a turn…
    for (let sq = 0; sq < 64; sq++) {
      const p = b[sq];
      if (!(p & bit)) continue;
      const t = p & TYPE_MASK;
      if (t === FROZEN_QUEEN_2) b[sq] = FROZEN_QUEEN_1 | bit;
      else if (t === FROZEN_QUEEN_1) {
        b[sq] = QUEEN | bit;
        ctx.fx('thaw', sq);
      }
    }
    // …and a queen that just moved starts resting.
    if ((ctx.piece & TYPE_MASK) === QUEEN) {
      const to = moveTo(ctx.move);
      b[to] = FROZEN_QUEEN_2 | (ctx.piece & COLOR_MASK);
      ctx.fx('freeze', to);
    }
  },
};
